/**
 * Lazily load the Google Maps JS API (Places library).
 *
 * This used to be a render-blocking <script> in index.html, so every visitor
 * paid for it even though only the change-address form uses Places. Loading it
 * on demand keeps it off the critical path and out of the initial HTML.
 *
 * The promise is memoised, so concurrent callers share one <script> tag.
 */

const MAPS_KEY = import.meta.env.REACT_APP_GOOGLE_MAPS_KEY;

let mapsPromise = null;

export function loadGoogleMaps() {
  if (window.google?.maps?.places) {
    return Promise.resolve(window.google.maps);
  }

  if (!mapsPromise) {
    mapsPromise = new Promise((resolve, reject) => {
      if (!MAPS_KEY) {
        reject(new Error("REACT_APP_GOOGLE_MAPS_KEY is not configured"));
        return;
      }

      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_KEY}&libraries=places`;
      script.async = true;
      script.onload = () => {
        if (window.google?.maps?.places) {
          resolve(window.google.maps);
        } else {
          reject(new Error("Google Maps loaded without the Places library"));
        }
      };
      script.onerror = () => {
        // Allow a later attempt to retry rather than staying permanently rejected.
        mapsPromise = null;
        reject(new Error("Failed to load Google Maps"));
      };
      document.head.appendChild(script);
    });
  }

  return mapsPromise;
}
